import { useEffect, useState } from 'react';
import { BUILD_ID } from '@/constants/build';

/**
 * Runtime debug overlay — only renders when the URL contains
 *   ?zsdebug=chrome
 * AND the build is non-production.  Use this to triage chrome auto-hide
 * regressions from the device without needing remote-debug tooling.
 *
 * Live readout:
 *   - BUILD_ID
 *   - data-chrome   ("visible" / "hidden")
 *   - data-chrome-reason (last guard rail that fired)
 *   - scrollY
 *   - viewport height
 *   - safe-area-inset-top / bottom (resolved px)
 *   - measured nav height
 *   - measured header height
 *
 * Polls 4× / second via requestAnimationFrame so it never adds React
 * re-renders to the listener chain that the hook itself uses.
 */
export default function ChromeDebugOverlay() {
  const [enabled, setEnabled] = useState(false);
  const [snap, setSnap] = useState(null);

  useEffect(() => {
    // Disabled entirely outside dev builds — guards against accidentally
    // shipping the overlay to production.
    if (process.env.NODE_ENV === 'production') return undefined;
    const params = new URLSearchParams(window.location.search);
    // Accept both ?zsdebug=chrome (legacy) and ?zsdebug=ios (iOS-focused
    // variant — same overlay, just framed for iPhone debugging).
    const flag = params.get('zsdebug');
    if (flag !== 'chrome' && flag !== 'ios') return undefined;
    setEnabled(true);

    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:0;pointer-events:none;visibility:hidden;';
    document.body.appendChild(probe);
    const resolve = (expr) => {
      probe.style.height = expr;
      return getComputedStyle(probe).height;
    };

    let raf;
    let alive = true;
    const tick = () => {
      if (!alive) return;
      const html = document.documentElement;
      const nav = document.querySelector('.mobile-chrome-bottom');
      const top = document.querySelector('.mobile-chrome-top');
      const safeTop = resolve('env(safe-area-inset-top, 0px)');
      const safeBot = resolve('env(safe-area-inset-bottom, 0px)');
      const navRect = nav ? nav.getBoundingClientRect() : null;
      const navUl = nav ? nav.querySelector('ul') : null;
      const navUlRect = navUl ? navUl.getBoundingClientRect() : null;
      setSnap({
        build: BUILD_ID,
        ua: navigator.userAgent.length > 60 ? navigator.userAgent.slice(0, 60) + '…' : navigator.userAgent,
        chrome: html.dataset.chrome || '—',
        reason: html.dataset.chromeReason || '—',
        scrollY:
          document.body.scrollTop || document.documentElement.scrollTop || window.scrollY || 0,
        vh: window.innerHeight,
        vw: window.innerWidth,
        vvH: window.visualViewport ? Math.round(window.visualViewport.height) : '—',
        safeTop,
        safeBot,
        navH: navRect ? Math.round(navRect.height) : null,
        navRowH: navUlRect ? Math.round(navUlRect.height) : null,
        headerH: top ? Math.round(top.getBoundingClientRect().height) : null,
        bodyPB: getComputedStyle(document.body).paddingBottom,
        htmlOv: getComputedStyle(document.documentElement).overflow,
        bodyOv: getComputedStyle(document.body).overflowY,
      });
      raf = setTimeout(() => requestAnimationFrame(tick), 250);
    };
    tick();

    return () => {
      alive = false;
      if (raf) clearTimeout(raf);
      probe.remove();
    };
  }, []);

  if (!enabled || !snap) return null;

  const row = (k, v) => (
    <div className="flex justify-between gap-3" key={k}>
      <span className="text-[#94A3B8]">{k}</span>
      <span className="font-mono">{String(v)}</span>
    </div>
  );

  return (
    <div
      data-testid="chrome-debug-overlay"
      className="fixed top-2 left-2 z-[9999] max-w-[240px] rounded-lg bg-black/85 backdrop-blur-md border border-[#FFD166]/40 px-3 py-2 text-[10px] leading-snug text-white shadow-2xl pointer-events-none select-text"
    >
      <div className="text-[#FFD166] font-bold mb-1">ZS · chrome debug</div>
      {row('build', snap.build)}
      {row('ua', snap.ua)}
      {row('chrome', snap.chrome)}
      {row('reason', snap.reason)}
      {row('scrollY', snap.scrollY)}
      {row('vw × vh', `${snap.vw}×${snap.vh}`)}
      {row('visualVH', snap.vvH)}
      {row('safe top', snap.safeTop)}
      {row('safe bot', snap.safeBot)}
      {row('nav total', snap.navH ?? '—')}
      {row('nav row',   snap.navRowH ?? '—')}
      {row('hdr h', snap.headerH ?? '—')}
      {row('body pb', snap.bodyPB)}
      {row('html ov', snap.htmlOv)}
      {row('body ov', snap.bodyOv)}
    </div>
  );
}
